import { useThemeContext } from "@src/context/ThemeContext";
import type { ThemeIconName } from "@src/interfaces/theme-config.interface";
import { IconBrand } from "@uhg-abyss/web/ui/IconBrand";
import type { ComponentProps, CSSProperties, FC, ImgHTMLAttributes } from "react";

interface ThemedIconProps {
  /** Icon key used to look up icon metadata in the active theme config */
  themeIconName: ThemeIconName;
  /** Optional alt text fallback when not provided by theme config */
  altText?: string;
  /** Optional test id forwarded to themed `<img>` */
  imgId?: string;
  /** Style overrides merged with theme icon style for themed `<img>` renders */
  themedIconStyle?: CSSProperties;
  /** Additional attributes forwarded to the themed `<img>` element */
  themedImgProps?: Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "alt" | "style">;
  /** Props forwarded to `IconBrand` when no themed icon image is rendered */
  iconBrandProps?: ComponentProps<typeof IconBrand>;
}

/**
 * Renders a theme-aware icon that uses themed assets when available.
 *
 * Resolution order:
 * 1. If active theme explicitly marks the icon hidden -> `null`
 * 2. Else if themed icon asset exists for `themeIconName` -> themed `<img>`
 * 3. Else if `iconBrandProps` exists -> `<IconBrand>` fallback
 * 4. Otherwise -> `null`
 */
export const ThemedIcon: FC<ThemedIconProps> = ({
  themeIconName,
  altText,
  imgId,
  themedIconStyle,
  themedImgProps,
  iconBrandProps,
}) => {
  const { activeThemeConfig } = useThemeContext();
  const themeIcon = activeThemeConfig.assets.icons[themeIconName];

  if (themeIcon?.hidden) {
    return null;
  }

  if (themeIcon?.src) {
    return (
      <img
        src={themeIcon.src}
        alt={themeIcon.alt || altText || "Icon"}
        id={imgId}
        style={{ ...(themeIcon.style || {}), ...themedIconStyle }}
        {...themedImgProps}
      />
    );
  }

  if (iconBrandProps) {
    return <IconBrand {...iconBrandProps} />;
  }

  return null;
};
